// src/components/GcRequiredReading.js
//
// SECTION 520 (2026-09-30). Bob: "The white paper to my right must be presented to the gcs upon on-boarding, up to them
// to share it with their lcs." The white paper "Carrier Electronic Claim Filing and the Duty of Fair Representation" is
// shown at the top of the General Chairperson's desk as required reading. The file is held privately in Storage at
// reference/gc/ - storage.rules lets a General Chairperson and the administrator read it and nobody else; it is not in
// the website's public files and not in the code repository. Sharing it with his Local Chairpersons is the GC's call.
//
// SECTION 522 (2026-09-30). Bob: "The whitepaper should only be open when they on-board." The card is now an
// onboarding step, not a fixture: it shows until the General Chairperson opens the paper and confirms he has read it.
// That confirmation is recorded on his own profile (users/{uid}.gcWhitePaperReadAt, server time - a self-owner clause
// in firestore.rules that lets him set that one field and nothing else), and from then on the card is gone.
import React, { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { profilePatch } from '../data/memberProfile';   // PRF-1: every write-back to users/ passes through profilePatch()

export const GC_WHITE_PAPER = {
  path: 'reference/gc/Carrier_Electronic_Claim_Filing_and_DFR_White_Paper.pdf',
  title: 'Carrier Electronic Claim Filing and the Duty of Fair Representation',
};

export default function GcRequiredReading({ user }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const [readAt, setReadAt] = useState(undefined);   // undefined = not yet known; null = not acknowledged
  const eligible = !!user && (user.isAdmin || user.role === 'general_chair');
  useEffect(() => {
    let dead = false;
    if (!eligible || !user.uid) return undefined;
    getDoc(doc(db, 'users', user.uid))
      .then(d => { if (!dead) setReadAt(d.exists() && d.data().gcWhitePaperReadAt ? d.data().gcWhitePaperReadAt : null); })
      .catch(() => { if (!dead) setReadAt(null); });
    return () => { dead = true; };
  }, [eligible, user && user.uid]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!eligible || readAt === undefined || readAt) return null;   // onboarding done: the card is gone
  const acknowledge = async () => {
    setBusy(true); setErr('');
    try {
      await setDoc(doc(db, 'users', user.uid), profilePatch({ gcWhitePaperReadAt: serverTimestamp() }), { merge: true });
      setReadAt(true);
    } catch (e) {
      setErr('Your confirmation could not be recorded (' + ((e && e.code) || 'unavailable') + '). Please try again.');
    } finally { setBusy(false); }
  };
  const open = async () => {
    setBusy(true); setErr('');
    try {
      const url = await getDownloadURL(ref(storage, GC_WHITE_PAPER.path));
      window.open(url, '_blank', 'noopener,noreferrer');
      setOpened(true);
    } catch (e) {
      setErr('The white paper could not be opened (' + ((e && e.code) || 'unavailable') + ').');
    } finally { setBusy(false); }
  };
  return (
    <div style={{ border: '2px solid #E8A020', background: '#0D1B2A', borderRadius: 8, padding: '14px 18px', margin: '10px 0 16px' }}>
      <div style={{ fontSize: 11, color: '#E8A020', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Onboarding - required reading for General Chairpersons</div>
      <div style={{ fontSize: 15, color: '#DCE7F2', fontWeight: 700, margin: '4px 0 6px' }}>{GC_WHITE_PAPER.title}</div>
      <div style={{ fontSize: 12, color: '#8AA4BF', lineHeight: 1.6, marginBottom: 10 }}>
        How claims are filed with your carrier - and whether any electronic filing arrangement protects your members - is part of
        your duty of fair representation. Read this before you take claims on the platform. It is yours to share with your Local
        Chairpersons as you see fit; it is held privately and is not shown to members.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={open}>{busy && !opened ? 'Opening…' : 'Open the white paper (PDF)'}</button>
        <button className="btn btn-secondary btn-sm" disabled={busy || !opened} onClick={acknowledge}
          title={opened ? '' : 'Open the white paper first'}>I have read it - finish this step</button>
        <span style={{ fontSize: 11, color: '#8AA4BF' }}>{opened ? 'Save a copy if you want to keep it or share it; this card will not appear again.' : 'Open it first; then confirm.'}</span>
      </div>
      {err ? <div style={{ fontSize: 12, color: '#E06C6C', marginTop: 6 }}>{err}</div> : null}
    </div>
  );
}
